/**
 * Delete Blog Feature
 * 100% Isolated: Delete blog functionality that can be changed independently
 */
import { useState, useCallback } from 'react';
import { blogApiService } from '../api';
import { userService } from '../../user/service';
import { logger } from '@/shared/utils';

export const useDeleteBlog = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteBlog = useCallback(async (blogId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      logger.debug('Starting delete', 'useDeleteBlog', { blogId });
      
      // Check authentication
      if (!userService.isAuthenticated()) {
        logger.warn('User not authenticated', 'useDeleteBlog');
        throw new Error('Authentication required to delete a blog');
      }

      logger.debug('Calling delete API', 'useDeleteBlog', { blogId });
      // Delete blog via API
      const response = await blogApiService.deleteBlog(blogId);
      logger.debug('Delete API response received', 'useDeleteBlog', { blogId, success: response.success });
      
      // Check if the response indicates success
      if (!response.success) {
        logger.error('Delete failed', 'useDeleteBlog', { blogId, error: response.error });
        throw new Error(response.error?.message || 'Failed to delete blog');
      }
      
      logger.info('Blog deleted successfully', 'useDeleteBlog', { blogId });
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete blog';
      setError(errorMessage);
      logger.error('Delete error', 'useDeleteBlog', { blogId, error: err });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    deleteBlog,
    loading,
    error,
    clearError: () => setError(null)
  };
};
